// Two responsibilities:
// 1. injecting the gtag.js script and preparing the GTM dataLayer on init
// 2. fanning out track/page/identify calls to GA4 (gtag) and GTM (dataLayer)
//
// The GTM container script itself is deliberately NOT injected here - it is fetched late by
// `components/common/scripts/GoogleTagManager.tsx`. See `initDataLayer` below.

// prod id - 'G-Y7R60ZR1PY'
const gaId = process.env.googleAnalyticsId
// prod id - 'GTM-W2N329G'
const gtmId = process.env.googleTagManagerId

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
    gtag?: (...args: unknown[]) => void
  }
}

let gaInitialized = false
let dataLayerInitialized = false

const injectScript = (src: string) => {
  const script = document.createElement('script')
  script.src = src
  script.async = true
  document.head.appendChild(script)
}

// The queue GTM replays on arrival. It has to exist from the first tracked call onwards, even
// though the container is fetched much later - that is what makes deferring the container
// loss-free for anything this app pushes.
const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer ?? []
}

const initGA4 = () => {
  if (gaInitialized || !gaId) {
    return
  }

  ensureDataLayer()
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments as unknown as Record<string, unknown>)
  }

  injectScript(`https://www.googletagmanager.com/gtag/js?id=${gaId}`)
  window.gtag('js', new Date())
  // `send_page_view: false` because `gaPage` is the single source of page views. GA4's default
  // auto-page-view would fire here as well, and the app then sends its own on mount - the same
  // pageview counted twice.
  window.gtag('config', gaId, {send_page_view: false})

  gaInitialized = true
}

// Queues the `gtm.js` message the standard GTM snippet pushes, without fetching the container.
//
// The message has to be queued *now* rather than alongside the deferred fetch: GTM replays
// `dataLayer` in order on arrival, and most base tags (Meta pixel, UET) hang off the Page View
// trigger, which is the `gtm.js` event. Anything queued ahead of it is evaluated first, so a
// conversion event pushed before `gtm.js` would fire its event tag before the base tag exists -
// `fbq` undefined, event silently dropped. Keeping this push early pins `gtm.js` to index 0.
const initDataLayer = () => {
  if (dataLayerInitialized || !gtmId) {
    return
  }

  ensureDataLayer()
  window.dataLayer?.push({'gtm.start': new Date().getTime(), event: 'gtm.js'})

  dataLayerInitialized = true
}

// Client-side only, and empty when no container is configured (dev/preview).
export const gtmContainerSrc = gtmId
  ? `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
  : undefined

export const initGoogleAnalytics = () => {
  if (typeof window === 'undefined') {
    return
  }
  initGA4()
  initDataLayer()
}

// Lazily initializes GA/GTM before any call so tracking from a deep component
// effect (which runs before the root layout's init effect) isn't dropped
const ensureReady = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  initGoogleAnalytics()
  return true
}

// Keys stripped from the GA4-direct (gtag) path only — GA4 prohibits raw PII.
// The GTM dataLayer intentionally keeps full properties: GTM tags (e.g. Google
// Ads enhanced conversions) need PII like email to hash it themselves. `name` is
// excluded
const PII_KEYS = new Set([
  'email',
  'contact_email',
  'firstName',
  'first_name',
  'lastName',
  'last_name',
  'phone',
  'phone_number',
])

const stripPii = (properties?: Record<string, unknown>): Record<string, unknown> => {
  if (!properties) {
    return {}
  }
  return Object.fromEntries(Object.entries(properties).filter(([key]) => !PII_KEYS.has(key)))
}

export const gaTrack = (event: string, properties?: Record<string, unknown>) => {
  if (!ensureReady()) {
    return
  }
  if (gaId) {
    window.gtag?.('event', event, stripPii(properties))
  }
  if (gtmId) {
    // canonical key last so a property named `event` can't clobber the event name
    window.dataLayer?.push({...properties, event})
  }
}

export const gaPage = (properties?: Record<string, unknown>) => {
  if (!ensureReady()) {
    return
  }
  if (gaId) {
    window.gtag?.('event', 'page_view', stripPii(properties))
  }
  if (gtmId) {
    window.dataLayer?.push({...properties, event: 'page_view'})
  }
}

export const gaIdentify = (userId: string, traits?: Record<string, unknown>) => {
  if (!ensureReady()) {
    return
  }
  if (gaId) {
    // GA4 direct: only the non-PII user_id, never PII user_properties.
    window.gtag?.('set', {user_id: userId})
  }
  if (gtmId) {
    // dataLayer keeps traits so GTM enhanced-conversion tags can hash them.
    window.dataLayer?.push({...traits, event: 'identify', userId})
  }
}
